import { BrainCircuit } from 'lucide-react'
import { SiteLayout } from '../components/ui/site-chrome'
import { TrustPage } from '../components/ui/trust-page'

export default function AiStrategyPage() {
  return (
    <SiteLayout
      current="/ai-strategy"
      title="AI Strategy & Implementation | Zadro"
      description="AI strategy services for companies that want practical automation, LLM product design, governance, and workflow transformation without hype."
    >
      <TrustPage
        Icon={BrainCircuit}
        visual="ai"
        eyebrow="AI Strategy"
        heroTitle="Use AI where it creates leverage, not theater."
        heroCopy="I help companies separate useful AI from expensive noise. The work starts by finding the workflow, data, and decision points where AI can improve speed, quality, margin, or customer experience."
        proof={[
          'Business-first AI opportunity mapping before tool selection.',
          'Hands-on full-stack background for evaluating what can actually ship.',
          'Governance, risk, and production ownership included from the start.',
        ]}
        cards={[
          {
            title: 'AI opportunity map',
            copy: 'Identify the workflows where AI can reduce cost, accelerate revenue, improve service quality, or unlock a new product surface.',
          },
          {
            title: 'LLM product design',
            copy: 'Design agentic workflows, context systems, evaluation loops, and user experience patterns that can survive real usage.',
          },
          {
            title: 'Production readiness',
            copy: 'Review data access, privacy, vendor risk, monitoring, cost, failure modes, and human-in-the-loop controls.',
          },
        ]}
        sections={[
          {
            kicker: 'Common Failure',
            title: 'AI pilots fail when nobody owns the circuit.',
            copy: 'A model demo is not a business system. The real work is connecting data, workflows, interfaces, permissions, review, and measurable outcomes.',
            items: [
              'AI tools adopted by departments without a shared operating model',
              'Prompt demos that never become reliable production workflows',
              'No clear evaluation loop for quality, risk, or ROI',
              'Vendor choices made before internal process and data readiness are understood',
            ],
          },
          {
            kicker: 'How We Work',
            title: 'From AI idea to deployable workflow.',
            copy: 'We map the business process, design the AI-assisted flow, identify constraints, and then decide what should be automated, augmented, or left human.',
            items: [
              'Workflow and data inventory',
              'Use-case scoring by value, risk, feasibility, and speed',
              'Prototype scope for internal tools, agents, or customer-facing AI',
              'Implementation plan with governance, testing, and rollout ownership',
            ],
          },
        ]}
        beforeCta={
          <section className="border-y border-slate-800 bg-slate-950">
            <div className="mx-auto max-w-7xl px-5 py-20 sm:px-8">
              <div className="mb-10 grid gap-6 lg:grid-cols-[0.72fr_1fr] lg:items-end">
                <div>
                  <p className="section-kicker">Motion Study</p>
                  <h2 className="section-title">See the AI operating model in motion.</h2>
                </div>
                <p className="max-w-2xl leading-7 text-slate-300 lg:justify-self-end">
                  This short Remotion sequence visualizes the difference between an AI demo and an AI
                  system: workflow mapping, data and context routing, evaluation, governance, and a
                  production handoff the business can operate.
                </p>
              </div>
              <div className="overflow-hidden rounded-[1.75rem] border border-slate-800 bg-slate-900 p-3 shadow-[0_30px_100px_rgba(2,6,23,0.42)] sm:p-5">
                <video
                  className="aspect-video h-auto w-full rounded-[1.25rem] bg-slate-950 object-contain"
                  src="/media/ai-strategy-motion.mp4"
                  poster="/media/ai-strategy-motion-poster.png"
                  autoPlay
                  loop
                  muted
                  playsInline
                  preload="metadata"
                />
              </div>
              <div className="mt-5 grid gap-3 text-sm text-slate-300 sm:grid-cols-2 lg:grid-cols-4">
                  {[
                    'Map where AI enters the workflow',
                    'Route context before choosing tools',
                    'Evaluate quality before scaling usage',
                    'Wrap the system with governance and ownership',
                  ].map((item) => (
                    <div key={item} className="rounded-2xl border border-slate-800 bg-slate-900/70 p-4">
                      {item}
                    </div>
                  ))}
              </div>
            </div>
          </section>
        }
      />
    </SiteLayout>
  )
}
